import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Policy2, PolicyPriceChangeResponse } from '../types/policy';
import {
	RenewalActionRequest,
	RenewalCreateRequest,
	RenewalInvalidateRequest,
	RenewalResponse,
} from '../types/renewals';
import { getApiUrl } from '../util/util';
import { get, patch, post } from './http';

interface RenewalsApi {
	getRenewalsForPolicy(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<RenewalResponse[]>>;
	getPolicyIfRenewalIssued(
		authorizationToken: string,
		renewalLocator: string,
	): Promise<AxiosResponse<Policy2>>;
	createRenewal(
		authorizationToken: string,
		policyLocator: string,
		renewalCreateReq: RenewalCreateRequest,
	): Promise<AxiosResponse<RenewalResponse>>;
	getRenewal(
		authorizationToken: string,
		renewalLocator: string,
	): Promise<AxiosResponse<RenewalResponse>>;
	updateRenewal(
		authorizationToken: string,
		renewalLocator: string,
		actionReq: RenewalActionRequest,
	): Promise<AxiosResponse<RenewalResponse>>;
	invalidateRenewal(
		authorizationToken: string,
		renewalLocator: string,
		actionReq: RenewalInvalidateRequest,
	): Promise<AxiosResponse<RenewalResponse>>;
	acceptRenewal(
		authorizationToken: string,
		renewalLocator: string,
	): Promise<AxiosResponse<RenewalResponse>>;
	discardRenewal(
		authorizationToken: string,
		renewalLocator: string,
	): Promise<AxiosResponse<RenewalResponse>>;
	quoteRenewal(
		authorizationToken: string,
		renewalLocator: string,
	): Promise<AxiosResponse<RenewalResponse>>;
	issueRenewal(
		authorizationToken: string,
		renewalLocator: string,
	): Promise<AxiosResponse<RenewalResponse>>;
	previewRenewalPrice(
		authorizationToken: string,
		renewalLocator: string,
	): Promise<AxiosResponse<PolicyPriceChangeResponse>>;
}

export default {
	async getRenewalsForPolicy(
		authorizationToken: string,
		policyLocator: string,
		config?: AxiosRequestConfig<any>,
	) {
		return get(
			`${getApiUrl()}/policies/${policyLocator}/renewals`,
			authorizationToken,
			config,
		);
	},
	async getPolicyIfRenewalIssued(
		authorizationToken: string,
		renewalLocator: string,
	) {
		return get(
			`${getApiUrl()}/renewals/${renewalLocator}/policyWhenIssued`,
			authorizationToken,
		);
	},
	async createRenewal(
		authorizationToken: string,
		policyLocator: string,
		renewalCreateReq: RenewalCreateRequest,
	) {
		return post(
			`${getApiUrl()}/policies/${policyLocator}/renewals`,
			authorizationToken,
			renewalCreateReq,
		);
	},
	async getRenewal(authorizationToken: string, renewalLocator: string) {
		return get(`${getApiUrl()}/renewals/${renewalLocator}`, authorizationToken);
	},
	async updateRenewal(
		authorizationToken: string,
		renewalLocator: string,
		actionReq: RenewalActionRequest,
	) {
		return patch(
			`${getApiUrl()}/renewals/${renewalLocator}/update`,
			authorizationToken,
			actionReq,
		);
	},
	async discardRenewal(authorizationToken: string, renewalLocator: string) {
		return patch(
			`${getApiUrl()}/renewals/${renewalLocator}/discard`,
			authorizationToken,
			{},
		);
	},
	async quoteRenewal(authorizationToken: string, renewalLocator: string) {
		return patch(
			`${getApiUrl()}/renewals/${renewalLocator}/quote`,
			authorizationToken,
			{ conflictHandling: 'invalidate' },
		);
	},
	async acceptRenewal(authorizationToken: string, renewalLocator: string) {
		return patch(
			`${getApiUrl()}/renewals/${renewalLocator}/accept`,
			authorizationToken,
			{ conflictHandling: 'invalidate' },
		);
	},
	async issueRenewal(authorizationToken: string, renewalLocator: string) {
		return patch(
			`${getApiUrl()}/renewals/${renewalLocator}/issue`,
			authorizationToken,
			{ conflictHandling: 'invalidate' },
		);
	},
	async invalidateRenewal(
		authorizationToken: string,
		renewalLocator: string,
		actionRequest: RenewalInvalidateRequest,
	) {
		return patch(
			`${getApiUrl()}/renewals/${renewalLocator}/invalidate`,
			authorizationToken,
			actionRequest,
		);
	},
	async previewRenewalPrice(
		authorizationToken: string,
		renewalLocator: string,
	) {
		return get(
			`${getApiUrl()}/renewals/${renewalLocator}/price`,
			authorizationToken,
		);
	},
} as RenewalsApi;
